import { Button, Checkbox, DatePicker, Empty, Input, Select, Typography } from 'antd';
import { Paperclip } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { FormFieldModel } from '../types';

const { Text, Paragraph } = Typography;

interface FormPreviewProps {
  fields: FormFieldModel[];
  kvkkText: string;
}

function FieldControl({ field, placeholder }: { field: FormFieldModel; placeholder: string }) {
  switch (field.type) {
    case 'textarea':
      return <Input.TextArea rows={3} disabled maxLength={field.maxLength ?? undefined} />;
    case 'email':
      return <Input disabled type="email" maxLength={field.maxLength ?? undefined} />;
    case 'phone':
      return <Input disabled inputMode="tel" maxLength={field.maxLength ?? undefined} />;
    case 'select':
      return (
        <Select
          disabled
          placeholder={placeholder}
          style={{ width: '100%' }}
          options={field.options.filter((o) => o.trim()).map((o) => ({ value: o, label: o }))}
        />
      );
    case 'checkbox':
      return <Checkbox disabled>{field.label}</Checkbox>;
    case 'date':
      return <DatePicker disabled style={{ width: '100%' }} />;
    case 'file':
      return (
        <Button disabled icon={<Paperclip size={14} />}>
          {field.label}
        </Button>
      );
    default:
      return <Input disabled maxLength={field.maxLength ?? undefined} />;
  }
}

/** Alan listesinden basit, salt-okunur render — admin-içi canlı önizleme (SCR-ADM-FRM-1). */
export function FormPreview({ fields, kvkkText }: FormPreviewProps) {
  const { t } = useTranslation('forms');

  if (fields.length === 0) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('builder.preview.empty')} />;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }} data-testid="form-preview">
      {fields.map((field) => (
        <div key={field.id}>
          {field.type !== 'checkbox' ? (
            <div style={{ marginBottom: 4 }}>
              <Text>{field.label.trim() || field.key.trim()}</Text>
              {field.required ? <Text type="danger"> *</Text> : null}
            </div>
          ) : null}
          <FieldControl field={field} placeholder={t('builder.preview.selectPlaceholder')} />
        </div>
      ))}

      {kvkkText.trim() ? (
        <div>
          <Checkbox disabled>{t('builder.preview.kvkkConsent')}</Checkbox>
          <Paragraph type="secondary" style={{ fontSize: 12, marginTop: 4, marginBottom: 0, whiteSpace: 'pre-wrap' }}>
            {kvkkText}
          </Paragraph>
        </div>
      ) : null}

      <div>
        <Button type="primary" disabled>
          {t('builder.preview.submit')}
        </Button>
      </div>
    </div>
  );
}
